class BackInStockSubscriptionForm {
  constructor(
    container,
    isB2B,
    email,
    variantId,
    isVariantInStock,
    isAlreadySubscribed,
    alreadySubscribedText
  ) {
    this.container = container;
    this.isB2B = isB2B;
    this.email = email;
    this.variantId = variantId;
    this.isVariantInStock = isVariantInStock;
    this.isAlreadySubscribed = isAlreadySubscribed;
    this.alreadySubscribedText = alreadySubscribedText;

    if (this.container) {
      this.initObserver();
    }
  }

  initObserver() {
    this.observer = new MutationObserver(() => this.checkForShadowRoot());
    this.observer.observe(this.container, { childList: true, subtree: true });
    this.checkForShadowRoot(); // initial call so observer can check for mutations
  }

  checkForShadowRoot() {
    // Remove container if variant is in stock
    if (this.isVariantInStock === 'true' || this.isB2B !== 'true') {
      this.observer.disconnect();
      this.container.remove();
      return;
    }

    const shopifyFormsEmbed = this.container.querySelector(
      'shopify-forms-embed'
    );
    if (!shopifyFormsEmbed || !shopifyFormsEmbed.shadowRoot) {
      return;
    }

    // Stop observing the container
    this.observer.disconnect();

    // Now observe the shadow DOM for form elements
    this.shadowObserver = new MutationObserver(() => this.updateForm());
    this.shadowObserver.observe(shopifyFormsEmbed.shadowRoot, {
      childList: true,
      subtree: true,
    });

    // Try immediately in case form is already there
    this.updateForm();
  }

  updateForm() {
    const shopifyFormsEmbed = this.container.querySelector(
      'shopify-forms-embed'
    );
    const formsShadowRoot = shopifyFormsEmbed?.shadowRoot;
    if (!formsShadowRoot) {
      return;
    }

    const emailInput = formsShadowRoot.querySelector('input#email');
    const variantInput = formsShadowRoot.querySelector(
      'input#custom\\#product_variant'
    );
    const button = formsShadowRoot.querySelector('button[type="submit"]');

    // Wait until the form elements are available in the shadow DOM
    if (!emailInput || !variantInput) {
      return;
    }

    // Stop observing the shadow DOM
    this.shadowObserver?.disconnect();

    if (this.isAlreadySubscribed === 'true' && button) {
      button.disabled = true;
      button.textContent = this.alreadySubscribedText || button.textContent;
    }

    if (this.email) {
      const emailContainer = emailInput.parentElement;
      if (emailContainer) {
        emailContainer.style.display = 'none';
      }
      emailInput.value = this.email;
      emailInput.dispatchEvent(new Event('input', { bubbles: true }));
      emailInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const variantContainer = variantInput.parentElement;
    if (variantContainer) {
      variantContainer.style.display = 'none';
    }
    variantInput.value = this.variantId;
    variantInput.dispatchEvent(new Event('input', { bubbles: true }));
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function initPdpBackInStock() {
  const scriptTag = document.querySelector(
    'script[src*="back-in-stock-subscription.js"][data-forms-id]'
  );
  if (!scriptTag) {
    return;
  }

  const formsId = scriptTag.dataset.formsId;
  if (!formsId) {
    return;
  }

  const isB2B = scriptTag.dataset.customerIsB2b;
  const email = scriptTag.dataset.customerMail;
  const variantId = scriptTag.dataset.productVariant;
  const isVariantInStock = scriptTag.dataset.productVariantInStock;
  const isAlreadySubscribed = scriptTag.dataset.alreadySubscribed;
  const alreadySubscribedText = scriptTag.dataset.alreadySubscribedText;

  const backInStockSubscriptionForms = document.querySelectorAll(
    `div[data-forms-id="forms-root-${formsId}"]`
  );

  backInStockSubscriptionForms.forEach((container) => {
    new BackInStockSubscriptionForm(
      container,
      isB2B,
      email,
      variantId,
      isVariantInStock,
      isAlreadySubscribed,
      alreadySubscribedText
    );
  });
}

function initOverlayBackInStock() {
  const overlayModal = document.getElementById('BackInStockFormOverlay');
  const triggerButtons = document.querySelectorAll(
    '[data-back-in-stock-overlay-trigger="true"]'
  );

  if (!overlayModal) {
    triggerButtons.forEach((button) => {
      const soldOutLabel = button.dataset.soldOutLabel || '';
      const labelElement = button.querySelector('[data-back-in-stock-label]');
      button.disabled = true;
      button.removeAttribute('aria-controls');
      button.removeAttribute('aria-haspopup');
      if (soldOutLabel) {
        button.setAttribute('aria-label', soldOutLabel);
        if (labelElement) {
          labelElement.textContent = soldOutLabel;
        }
      }
    });
    return;
  }

  let activeConfig = null;

  const applyOverlayConfig = () => {
    if (!activeConfig?.formsId || !activeConfig.variantId) {
      return;
    }

    const containers = overlayModal.querySelectorAll(
      `div[data-forms-id="forms-root-${activeConfig.formsId}"]`
    );
    if (!containers.length) {
      return;
    }

    containers.forEach((container) => {
      new BackInStockSubscriptionForm(
        container,
        activeConfig.isB2B,
        activeConfig.email,
        activeConfig.variantId,
        activeConfig.isVariantInStock,
        activeConfig.isAlreadySubscribed,
        activeConfig.alreadySubscribedText
      );
    });

  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-back-in-stock-overlay-trigger="true"]');
    if (!trigger) {
      return;
    }

    activeConfig = {
      formsId: trigger.dataset.formsId,
      isB2B: 'true',
      email: trigger.dataset.customerMail || '',
      variantId: trigger.dataset.productVariant || '',
      isVariantInStock: 'false',
      isAlreadySubscribed: trigger.dataset.alreadySubscribed || 'false',
      alreadySubscribedText: trigger.dataset.alreadySubscribedText || '',
    };
    applyOverlayConfig();
  });

  const modalObserver = new MutationObserver(() => {
    applyOverlayConfig();
  });

  modalObserver.observe(overlayModal, { childList: true, subtree: true });
}

if (!window.__backInStockSubscriptionUnifiedInitialized) {
  window.__backInStockSubscriptionUnifiedInitialized = true;
  const runInit = () => {
    initPdpBackInStock();
    initOverlayBackInStock();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
  } else {
    runInit();
  }
}
